import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Save, Trash2, Globe2 } from "lucide-react";
import { toast } from "sonner";

const PAGE_FIELDS = {
  home: [
    { key: "hero_title", label: "Hero title" },
    { key: "hero_subtitle", label: "Hero subtitle", textarea: true },
    { key: "about_title", label: "About section title" },
    { key: "about_body", label: "About section body", textarea: true },
    { key: "cta_title", label: "Quote CTA title" },
    { key: "cta_body", label: "Quote CTA body", textarea: true },
  ],
  services: [
    { key: "intro_title", label: "Page title" },
    { key: "intro_body", label: "Intro paragraph", textarea: true },
  ],
  contact: [
    { key: "intro", label: "Intro paragraph", textarea: true },
    { key: "email", label: "Contact email" },
    { key: "phone", label: "Contact phone" },
    { key: "address", label: "Address" },
    { key: "hours", label: "Hours line" },
  ],
};

const withKeys = (items) =>
  (items || []).map((it) => ({ ...it, _k: it._k || crypto.randomUUID() }));

export default function ContentEditor() {
  const [page, setPage] = useState("home");
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      if (data.services?.items) {
        data.services = { ...data.services, items: withKeys(data.services.items) };
      }
      setContent(data);
    } catch (e) {
      console.warn("[cms] content load failed", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!content) return <div className="text-slate-400 py-8 text-center">Loading content…</div>;
  const sections = content[page] || {};

  const setField = (key, value) => {
    setContent({ ...content, [page]: { ...sections, [key]: value } });
  };

  const setItem = (k, key, value) => {
    const items = (sections.items || []).map((it) => (it._k === k ? { ...it, [key]: value } : it));
    setField("items", items);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...sections };
      if (page === "services" && payload.items) {
        payload.items = payload.items.map(({ _k, ...rest }) => rest);
      }
      await api.patch(`/content/${page}`, payload);
      toast.success("Website content updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save content");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-display text-xl flex items-center gap-2">
          <Globe2 className="h-5 w-5" /> Website content
        </CardTitle>
        <Select value={page} onValueChange={setPage}>
          <SelectTrigger className="w-44" data-testid="content-page-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="home" data-testid="content-page-home">Home</SelectItem>
            <SelectItem value="services" data-testid="content-page-services">Our Services</SelectItem>
            <SelectItem value="contact" data-testid="content-page-contact">Contact Us</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="space-y-5">
        {PAGE_FIELDS[page].map((f) => (
          <div key={f.key} className="space-y-2">
            <Label>{f.label}</Label>
            {f.textarea ? (
              <Textarea rows={3} value={sections[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)}
                data-testid={`content-field-${f.key}`} />
            ) : (
              <Input value={sections[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)}
                data-testid={`content-field-${f.key}`} />
            )}
          </div>
        ))}

        {page === "services" && (
          <div className="space-y-4 border-t border-slate-200 pt-5">
            <div className="flex items-center justify-between">
              <Label>Service cards</Label>
              <Button variant="outline" size="sm" data-testid="add-service-item-btn"
                onClick={() => setField("items", [...(sections.items || []), { title: "", description: "", _k: crypto.randomUUID() }])}>
                <Plus className="h-4 w-4 mr-1" /> Add service
              </Button>
            </div>
            {(sections.items || []).map((item, idx) => (
              <div key={item._k} className="border border-slate-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">Service {idx + 1}</span>
                  <button className="text-slate-400 hover:text-rose-600"
                    data-testid={`remove-service-item-${idx}`}
                    onClick={() => setField("items", sections.items.filter((it) => it._k !== item._k))}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <Input value={item.title} placeholder="Service title"
                  data-testid={`service-item-title-${idx}`}
                  onChange={(e) => setItem(item._k, "title", e.target.value)} />
                <Textarea rows={2} value={item.description} placeholder="Service description"
                  data-testid={`service-item-desc-${idx}`}
                  onChange={(e) => setItem(item._k, "description", e.target.value)} />
              </div>
            ))}
          </div>
        )}

        <Button onClick={save} disabled={saving} data-testid="save-content-btn"
          className="rounded-full bg-[#0a2540] hover:bg-[#0a2540]/90">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Publish changes"}
        </Button>
      </CardContent>
    </Card>
  );
}
